import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';

const PRIORITY_STYLES: Record<string, { bg: string; text: string; icon: string }> = {
  Low:    { bg: '#F3F4F6', text: '#6B7280', icon: '🔵' },
  Normal: { bg: '#DBEAFE', text: '#1E40AF', icon: '🟢' },
  High:   { bg: '#FEF3C7', text: '#92400E', icon: '🟡' },
  Urgent: { bg: '#FEE2E2', text: '#991B1B', icon: '🔴' },
};

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  Open:         { bg: '#DBEAFE', text: '#1E40AF' },
  'In Progress': { bg: '#FEF3C7', text: '#92400E' },
  Done:         { bg: '#D1FAE5', text: '#065F46' },
};

interface TaskAssignmentProps {
  isAdmin: boolean;
  userId: string;
  userRole: string;
}

const TaskAssignment: React.FC<TaskAssignmentProps> = ({ isAdmin, userId, userRole }) => {
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('Open');
  const [form, setForm] = useState({
    title: '', description: '', assigned_to: '', assigned_role: '',
    priority: 'Normal', due_date: ''
  });

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const res = isAdmin
        ? await apiService.getAllCrmTasks()
        : await apiService.getCrmTasks(userId, userRole);
      if (res.success) setTasks(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchMeta = async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        apiService.getCrmUsers(),
        apiService.getCrmRoles()
      ]);
      if (usersRes.success) setUsers(usersRes.data);
      if (rolesRes.success) setRoles(rolesRes.data);
    } catch (e) {}
  };

  useEffect(() => { fetchTasks(); if (isAdmin) fetchMeta(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await apiService.createCrmTask({
        ...form,
        assigned_to: form.assigned_to || null,
        assigned_role: form.assigned_role || null,
        due_date: form.due_date || null,
        created_by: userId
      });
      setForm({ title: '', description: '', assigned_to: '', assigned_role: '', priority: 'Normal', due_date: '' });
      setShowForm(false);
      fetchTasks();
    } catch { alert('Error creating task'); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    try {
      await apiService.updateCrmTask(taskId, { status: newStatus, completed_by: userId });
      fetchTasks();
    } catch { alert('Error updating task'); }
  };

  const handleDelete = async (taskId: string) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await apiService.deleteCrmTask(taskId);
      fetchTasks();
    } catch {}
  };

  const filteredTasks = tasks.filter(t => {
    if (filter === 'all') return true;
    return t.status === filter;
  });

  const isOverdue = (dueDate: string, status: string) => {
    if (!dueDate || status === 'Done') return false;
    return new Date(dueDate) < new Date();
  };

  if (loading) return <div className="crm-loading"><div className="crm-spinner"></div> Loading tasks...</div>;

  return (
    <div>
      <div className="crm-page-header">
        <div className="crm-page-header-left">
          <h2>{isAdmin ? '📋 Task Management' : '📋 My Tasks'}</h2>
          <p>
            {isAdmin
              ? `${tasks.length} total tasks · Assign work to your team`
              : `${filteredTasks.length} task${filteredTasks.length !== 1 ? 's' : ''} assigned to you`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ padding: '7px 10px', border: '1.5px solid var(--crm-border)', borderRadius: '6px', fontSize: '13px' }}
          >
            <option value="all">All</option>
            <option value="Open">Open</option>
            <option value="In Progress">In Progress</option>
            <option value="Done">Done</option>
          </select>
          {isAdmin && (
            <button className="crm-btn crm-btn-primary" onClick={() => setShowForm(v => !v)}>
              {showForm ? '✕ Cancel' : '+ Assign Task'}
            </button>
          )}
          <button className="crm-btn crm-btn-ghost" onClick={fetchTasks}>↻</button>
        </div>
      </div>

      {/* Create Task Form (Admin Only) */}
      {showForm && isAdmin && (
        <form className="crm-form-panel" onSubmit={handleCreate}>
          <h3>📋 Assign New Task</h3>
          <div className="crm-form-fields">
            <div className="crm-form-field" style={{ gridColumn: '1 / -1' }}>
              <label>Task Title *</label>
              <input type="text" placeholder="e.g. Collect PAN card from Rahul" required
                value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
            </div>
            <div className="crm-form-field" style={{ gridColumn: '1 / -1' }}>
              <label>Description</label>
              <textarea placeholder="Details or instructions..." rows={2}
                style={{ padding: '8px 12px', border: '1.5px solid var(--crm-border)', borderRadius: '6px', fontFamily: 'Inter, sans-serif', fontSize: '13px', resize: 'vertical' }}
                value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
            </div>
            <div className="crm-form-field">
              <label>Assign to User</label>
              <select value={form.assigned_to} onChange={e => setForm({...form, assigned_to: e.target.value})}>
                <option value="">— Not specific user —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </div>
            <div className="crm-form-field">
              <label>Or Assign to Role</label>
              <select value={form.assigned_role} onChange={e => setForm({...form, assigned_role: e.target.value})}>
                <option value="">— Not specific role —</option>
                {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
              </select>
            </div>
            <div className="crm-form-field">
              <label>Priority</label>
              <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}>
                {Object.keys(PRIORITY_STYLES).map(p => <option key={p} value={p}>{PRIORITY_STYLES[p].icon} {p}</option>)}
              </select>
            </div>
            <div className="crm-form-field">
              <label>Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} />
            </div>
          </div>
          <div className="crm-form-actions">
            <button type="submit" className="crm-btn crm-btn-success" disabled={saving}>
              {saving ? 'Assigning…' : '✓ Assign Task'}
            </button>
          </div>
        </form>
      )}

      {/* Tasks List */}
      {filteredTasks.length === 0 ? (
        <div className="crm-empty-state">
          <div className="crm-empty-state-icon">✅</div>
          <h3>{filter === 'Done' ? 'No completed tasks yet' : 'No tasks found'}</h3>
          <p>{isAdmin ? 'Create a task to assign work to your team.' : 'You have no pending tasks right now.'}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredTasks.map((task: any) => {
            const pStyle = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.Normal;
            const sStyle = STATUS_STYLES[task.status] || STATUS_STYLES.Open;
            const overdue = isOverdue(task.due_date, task.status);

            return (
              <div key={task.id} style={{
                background: 'white', borderRadius: 'var(--crm-radius)', padding: '16px 20px',
                border: `1.5px solid ${overdue ? '#FECACA' : 'var(--crm-border)'}`,
                boxShadow: 'var(--crm-shadow)',
                borderLeft: `4px solid ${pStyle.text}`,
                transition: 'all 0.2s'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--crm-text-primary)' }}>
                        {pStyle.icon} {task.title}
                      </span>
                      {overdue && (
                        <span style={{ fontSize: '10px', fontWeight: 700, background: '#FEE2E2', color: '#991B1B', padding: '2px 6px', borderRadius: '4px' }}>
                          OVERDUE
                        </span>
                      )}
                    </div>
                    {task.description && (
                      <div style={{ fontSize: '12.5px', color: 'var(--crm-text-secondary)', marginBottom: 6, lineHeight: 1.5 }}>
                        {task.description}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: '11.5px' }}>
                      <span style={{ padding: '3px 8px', borderRadius: '12px', fontWeight: 700, background: sStyle.bg, color: sStyle.text }}>
                        {task.status}
                      </span>
                      <span style={{ padding: '3px 8px', borderRadius: '12px', fontWeight: 600, background: pStyle.bg, color: pStyle.text }}>
                        {task.priority}
                      </span>
                      {task.assigned_to?.name && (
                        <span style={{ color: 'var(--crm-text-muted)' }}>👤 {task.assigned_to.name}</span>
                      )}
                      {task.assigned_role && !task.assigned_to && (
                        <span style={{ color: 'var(--crm-text-muted)' }}>👥 {task.assigned_role}</span>
                      )}
                      {task.due_date && (
                        <span style={{ color: overdue ? '#991B1B' : 'var(--crm-text-muted)', fontWeight: overdue ? 700 : 400 }}>
                          📅 {new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </span>
                      )}
                      {task.created_by?.name && (
                        <span style={{ color: 'var(--crm-text-muted)' }}>from {task.created_by.name}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                    {task.status !== 'Done' && (
                      <>
                        {task.status === 'Open' && (
                          <button className="crm-btn crm-btn-ghost" style={{ padding: '5px 10px', fontSize: '11.5px' }}
                            onClick={() => handleStatusChange(task.id, 'In Progress')}>
                            ▶ Start
                          </button>
                        )}
                        <button className="crm-btn crm-btn-success" style={{ padding: '5px 10px', fontSize: '11.5px' }}
                          onClick={() => handleStatusChange(task.id, 'Done')}>
                          ✓ Done
                        </button>
                      </>
                    )}
                    {isAdmin && (
                      <button className="crm-btn" style={{ padding: '5px 8px', fontSize: '11px', color: 'var(--crm-danger)', background: 'white', border: '1px solid var(--crm-border)' }}
                        onClick={() => handleDelete(task.id)}>
                        🗑
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TaskAssignment;
